import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import {
  faHouseChimneyCrack,
  faTriangleExclamation,
} from '@fortawesome/pro-duotone-svg-icons'

import AppLayout from '../../components/app-layout/AppLayout'
import Button from '../../components/button/Button'
import Icon from '../../components/icon/Icon'
import DiscoverFilterBar from '../../components/discover-filter-bar/DiscoverFilterBar'
import DiscoverBuildingCard from '../../components/discover-building-card/DiscoverBuildingCard'
import DiscoverMap from '../../components/discover-map/DiscoverMap'
import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { searchDiscoverListings } from '../../_data/discover'
import {
  DiscoverFilters,
  DiscoverViewMode,
} from '../../_types/quickpass-discover'
import {
  EmptyBody,
  EmptyEmoji,
  EmptyState,
  EmptyTitle,
  HeroBlock,
  LoadMoreRow,
  PageStack,
  PageSubtitle,
  PageTitle,
  ResultsGrid,
  SkeletonCard,
  StatLabel,
  StatRow,
  ViewToggleButton,
  ViewToggleGroup,
} from './styled'
import {
  DEFAULT_PAGE_SIZE,
  buildDefaultFilters,
  buildFiltersFromProfile,
} from './helpers'
import { DiscoverPageState } from './types'

const DEBOUNCE_MS = 220

const initialiseState = (): DiscoverPageState => ({
  filters: buildDefaultFilters(),
  buildings: [],
  nextCursor: null,
  totalCount: 0,
  enabledPmCount: 0,
  isLoading: true,
  isLoadingMore: false,
  isError: false,
})

const dashboardContainerMotion = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04 },
  },
}

const dashboardItemMotion = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
}

/**
 * Renter-facing /discover surface. Filter bar at top, list of match-score-
 * ranked QC partner listings underneath. Map view is wired through the
 * toggle but renders a placeholder for now — Phase 4 lights it up.
 *
 * @returns {JSX.Element}
 */
const DiscoverPage = (): JSX.Element => {
  const { quickPassUser } = useAuth()
  const { profile } = useQuickPassProfile()
  const [state, setState] = useState<DiscoverPageState>(initialiseState)
  const [viewMode, setViewMode] = useState<DiscoverViewMode>('map')
  const hasPrefilledFromProfileReference = useRef<string | null>(null)
  const debounceHandleReference = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const runSearch = useCallback(
    async (
      filters: DiscoverFilters,
      cursor: number,
      shouldAppend: boolean,
    ): Promise<void> => {
      setState(previous => ({
        ...previous,
        isLoading: shouldAppend === false,
        isLoadingMore: shouldAppend === true,
        isError: false,
      }))

      try {
        const result = await searchDiscoverListings({
          filters,
          cursor,
          pageSize: DEFAULT_PAGE_SIZE,
          quickPassUserId: quickPassUser?.id ?? null,
        })

        setState(previous => ({
          ...previous,
          buildings:
            shouldAppend === true
              ? [...previous.buildings, ...result.buildings]
              : result.buildings,
          nextCursor: result.nextCursor,
          totalCount: result.totalCount,
          enabledPmCount: result.enabledPmCount,
          isLoading: false,
          isLoadingMore: false,
        }))
      } catch (error) {
        console.error('[discover] search failed', error)
        setState(previous => ({
          ...previous,
          isLoading: false,
          isLoadingMore: false,
          isError: true,
        }))
      }
    },
    [quickPassUser?.id],
  )

  useEffect(() => {
    if (profile === null) {
      return
    }

    if (hasPrefilledFromProfileReference.current === profile.id) {
      return
    }

    hasPrefilledFromProfileReference.current = profile.id

    setState(previous => ({
      ...previous,
      filters: buildFiltersFromProfile(profile),
    }))
  }, [profile])

  useEffect(() => {
    if (debounceHandleReference.current !== null) {
      clearTimeout(debounceHandleReference.current)
    }

    debounceHandleReference.current = setTimeout(() => {
      void runSearch(state.filters, 0, false)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceHandleReference.current !== null) {
        clearTimeout(debounceHandleReference.current)
      }
    }
  }, [state.filters, runSearch])

  const handleFiltersChange = useCallback((next: DiscoverFilters): void => {
    setState(previous => ({ ...previous, filters: next }))
  }, [])

  const handleResetFilters = useCallback((): void => {
    setState(previous => ({ ...previous, filters: buildDefaultFilters() }))
  }, [])

  const handleLoadMore = useCallback((): void => {
    if (state.nextCursor === null) {
      return
    }
    void runSearch(state.filters, state.nextCursor, true)
  }, [runSearch, state.filters, state.nextCursor])

  const hasResults = state.buildings.length > 0
  const showInitialSkeleton = state.isLoading === true && hasResults === false
  const showEmptyState =
    state.isLoading === false && hasResults === false && state.isError === false

  return (
    <AppLayout contentMaxWidth="wide">
      <PageStack
        variants={dashboardContainerMotion}
        initial="hidden"
        animate="visible"
      >
        <HeroBlock variants={dashboardItemMotion}>
          <PageTitle>Discover</PageTitle>
          <PageSubtitle>
            Browse every QuickCasa partner listing that fits your search. Save
            the ones that catch your eye, inquire the moment you&rsquo;re ready.
          </PageSubtitle>
        </HeroBlock>

        <motion.div variants={dashboardItemMotion}>
          <DiscoverFilterBar
            filters={state.filters}
            isLoading={state.isLoading}
            onFiltersChange={handleFiltersChange}
            onResetFilters={handleResetFilters}
          />
        </motion.div>

        <StatRow variants={dashboardItemMotion}>
          <StatLabel>
            {state.isLoading === true
              ? 'Searching…'
              : `${state.totalCount.toString()} listing${state.totalCount === 1 ? '' : 's'} match your filters`}
          </StatLabel>
          <ViewToggleGroup>
            <ViewToggleButton
              type="button"
              isActive={viewMode === 'list'}
              onClick={() => setViewMode('list')}
              aria-pressed={viewMode === 'list'}
            >
              List
            </ViewToggleButton>
            <ViewToggleButton
              type="button"
              isActive={viewMode === 'map'}
              onClick={() => setViewMode('map')}
              aria-pressed={viewMode === 'map'}
            >
              Map
            </ViewToggleButton>
          </ViewToggleGroup>
        </StatRow>

        {viewMode === 'map' ? (
          <DiscoverMap buildings={state.buildings} />
        ) : null}

        {viewMode === 'list' && showInitialSkeleton === true ? (
          <ResultsGrid>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </ResultsGrid>
        ) : null}

        {viewMode === 'list' && hasResults === true ? (
          <>
            <ResultsGrid variants={dashboardItemMotion}>
              {state.buildings.map(building => (
                <DiscoverBuildingCard
                  key={building.id}
                  building={building}
                  filtersInUse={state.filters}
                />
              ))}
            </ResultsGrid>
            {state.nextCursor !== null ? (
              <LoadMoreRow>
                <Button
                  type="button"
                  variant="secondary"
                  isLoading={state.isLoadingMore}
                  onClick={handleLoadMore}
                >
                  Load more
                </Button>
              </LoadMoreRow>
            ) : null}
          </>
        ) : null}

        {viewMode === 'list' && showEmptyState === true ? (
          <EmptyState>
            <EmptyEmoji aria-hidden="true">
              <Icon icon={faHouseChimneyCrack} label="House" />
            </EmptyEmoji>
            <EmptyTitle>
              {state.enabledPmCount === 0
                ? 'No property managers are live yet'
                : 'No matches for these filters'}
            </EmptyTitle>
            <EmptyBody>
              {state.enabledPmCount === 0
                ? 'When QuickCasa partner property managers turn on QuickPass Discover, their inventory will start showing up here.'
                : 'Loosen a filter or try a wider city. There are plenty of places out there — just none that match this exact mix.'}
            </EmptyBody>
          </EmptyState>
        ) : null}

        {state.isError === true ? (
          <EmptyState>
            <EmptyEmoji aria-hidden="true">
              <Icon icon={faTriangleExclamation} label="Warning" />
            </EmptyEmoji>
            <EmptyTitle>Search couldn&rsquo;t run</EmptyTitle>
            <EmptyBody>
              Something hiccuped on our side. Try changing a filter — that
              re-runs the search.
            </EmptyBody>
          </EmptyState>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default DiscoverPage
